import { useState } from 'react'
import { useMutation } from '@apollo/client/react'
import { Modal, Button, Group, Stack, Text, PasswordInput, Alert, List } from '@mantine/core'
import { IconCheck, IconAlertCircle } from '@tabler/icons-react'
import ResetPassword from '../graphql/queries/ResetPassword'
import { useModalStack } from '../ui/ModalStack'
import { useAppContext, useAppDispatch } from '../AppContext'
import { Actions } from '../AppReducer'

export default function ResetPasswordModal({ opened, onClose, email, resetToken, isNewAccount = false, roomId, roomName, ...props }) {
    const [password, setPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')
    const [message, setMessage] = useState('')
    const [messageType, setMessageType] = useState('error') // 'error' or 'success'
    const { pushModal, clearModals } = useModalStack()
    const appState = useAppContext()
    const dispatch = useAppDispatch()
    const isLoggedIn = appState?.loggedIn

    const [resetPasswordMutation, { loading }] = useMutation(ResetPassword)

    const title = isNewAccount ? 'Set Your Password' : 'Reset Password'
    const instructions = isNewAccount
        ? `Welcome! Please set a password for your new account${roomName ? ` to join ${roomName}` : ''}.`
        : 'Enter your new password below.'

    function passwordUpdated(e) {
        setMessage('')
        setPassword(e.target.value)
    }

    function confirmPasswordUpdated(e) {
        setMessage('')
        setConfirmPassword(e.target.value)
    }

    function validatePassword() {
        if (password.length < 8) {
            return 'Your password must be at least 8 characters long.'
        }
        if (password !== confirmPassword) {
            return 'Your password and the confirmation don\'t match.'
        }
        return null
    }

    async function resetPassword() {
        const passwordProblem = validatePassword()
        if (passwordProblem) {
            setMessage(passwordProblem)
            setMessageType('error')
            return
        }

        try {
            const response = await resetPasswordMutation({
                variables: {
                    email,
                    newPassword: password,
                    resetToken
                }
            })

            if (response.data?.resetPassword) {
                // Close this modal
                onClose?.()

                if (isLoggedIn) {
                    // User is already logged in, just show success and allow dismissal
                    // If this is a new account from an invite and we have a room ID, navigate to it
                    if (isNewAccount && roomId) {
                        dispatch(Actions.setCurrentRoom(roomId))
                    }
                    pushModal('alert', {
                        title: isNewAccount ? 'Account Created' : 'Password Reset Complete',
                        text: isNewAccount
                            ? 'Your account has been created successfully!'
                            : 'Your password has been successfully reset.',
                        okText: 'OK'
                    })
                } else {
                    // User is not logged in
                    // Clear all modals first to ensure clean state
                    clearModals()
                    setTimeout(() => {
                        // Show success message with an onOk callback to open login
                        pushModal('alert', {
                            title: 'Password Reset Complete',
                            text: 'Your password has been successfully reset. You may now log in with your new password.',
                            okText: 'Continue to Log In',
                            onOk: () => {
                                // This will be called when user clicks the button, then modal closes
                                // Push login modal after alert closes
                                setTimeout(() => {
                                    if (isNewAccount && roomId) {
                                        pushModal('emailLogin', { roomId })
                                    } else {
                                        pushModal('emailLogin')
                                    }
                                }, 100)
                            }
                        })
                    }, 100)
                }
            } else {
                setMessage('Failed to reset password. The reset link may have expired.')
                setMessageType('error')
            }
        } catch (err) {
            console.error('Reset password error:', err)
            setMessage(err.message || 'An error occurred. Please try again or request a new reset link.')
            setMessageType('error')
        }
    }

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={title}
            centered
            closeOnClickOutside={false}
            closeOnEscape={false}
            {...props}
        >
            <Stack gap="md">
                <Text size="sm" style={{ marginTop: '1em' }}>
                    {instructions}
                </Text>

                <PasswordInput
                    label="New Password"
                    placeholder="Enter new password"
                    value={password}
                    onChange={passwordUpdated}
                />

                <PasswordInput
                    label="Confirm Password"
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={confirmPasswordUpdated}
                />

                <Text size="xs" c="dimmed">
                    Password requirements:
                </Text>
                <List size="xs" c="dimmed" spacing="xs">
                    <List.Item>At least 8 characters long</List.Item>
                    <List.Item>Both password fields must match</List.Item>
                </List>

                {message && (
                    <Alert
                        color={messageType === 'success' ? 'green' : 'red'}
                        variant="light"
                        icon={messageType === 'success' ? <IconCheck size={16} /> : <IconAlertCircle size={16} />}
                    >
                        {message}
                    </Alert>
                )}

                <Group justify="flex-end" mt="xs">
                    <Button
                        variant="default"
                        onClick={onClose}
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                    <Button
                        disabled={loading}
                        loading={loading}
                        onClick={resetPassword}
                    >
                        Reset Password
                    </Button>
                </Group>
            </Stack>
        </Modal>
    )
}

